// FE side of the explainer template registry (labels, editable fields,
// defaults, constraints). Validation of record shape is server-authoritative
// (422 INVALID_CONTENT with per-scene details); the editor only enforces
// ranges and required text for immediate feedback.

import { DiagramLayout, SceneType } from './types';

export const SCENE_TYPES: SceneType[] = [
  'statement',
  'key_points',
  'two_column',
  'numbered_steps',
  'scenario_card',
  'stat_highlight',
  'diagram',
  'summary'
];

export const DIAGRAM_LAYOUTS: Array<{ value: DiagramLayout; label: string }> = [
  { value: 'spoke_and_wheel', label: 'Hub & spoke' },
  { value: 'hierarchy', label: 'Hierarchy' },
  { value: 'flow', label: 'Flow' },
  { value: 'grid_2x2', label: '2 × 2 grid' }
];

export const SCENE_ICONS = [
  'mail-warning',
  'shield',
  'lock',
  'user',
  'users',
  'handshake',
  'target',
  'growth',
  'chart',
  'clock',
  'calendar',
  'checklist',
  'lightbulb',
  'message',
  'phone',
  'document',
  'search',
  'warning',
  'star',
  'flag',
  'map-pin',
  'briefcase',
  'book',
  'gear',
  'globe',
  'heart',
  'link',
  'megaphone',
  'money',
  'trophy',
  'rocket',
  'eye'
];

export const LIMITS = {
  eyebrow: 32,
  title: 60,
  statement: 120,
  body: 260,
  point: 70,
  step: 70,
  takeaway: 90,
  label: 24,
  statValue: 14,
  statLabel: 90,
  source: 80,
  persona: 40,
  nodeLabel: 26,
  keyPoints: { min: 3, max: 5 },
  steps: { min: 2, max: 6 },
  takeaways: { min: 2, max: 3 },
  diagramNodes: { min: 2, max: 7 }
} as const;

export interface SceneTemplateMeta {
  type: SceneType;
  label: string;
  description: string;
  empty: () => Record<string, any>;
}

export const SCENE_TEMPLATES: Record<SceneType, SceneTemplateMeta> = {
  statement: {
    type: 'statement',
    label: 'Statement',
    description: 'One bold idea, centred.',
    empty: () => ({ statement: '' })
  },
  key_points: {
    type: 'key_points',
    label: 'Key points',
    description: 'Headline with 3–5 points.',
    empty: () => ({ eyebrow: null, title: '', points: ['', '', ''] })
  },
  two_column: {
    type: 'two_column',
    label: 'Two column',
    description: 'Text left, line-art visual right.',
    empty: () => ({ eyebrow: null, title: '', body: '', visual: { type: 'icon', name: 'lightbulb' } })
  },
  numbered_steps: {
    type: 'numbered_steps',
    label: 'Numbered steps',
    description: 'An ordered process.',
    empty: () => ({ eyebrow: null, title: '', steps: ['', ''] })
  },
  scenario_card: {
    type: 'scenario_card',
    label: 'Scenario card',
    description: 'A real-world situation.',
    empty: () => ({ label: 'SCENARIO', title: '', body: '', personaName: null, personaRole: null })
  },
  stat_highlight: {
    type: 'stat_highlight',
    label: 'Stat / highlight',
    description: 'One number or short quote.',
    empty: () => ({ value: '', label: '', source: null })
  },
  diagram: {
    type: 'diagram',
    label: 'Diagram',
    description: 'Relationships between concepts.',
    empty: () => ({
      eyebrow: null,
      title: '',
      layout: 'spoke_and_wheel',
      nodes: [
        { id: 'n1', label: '' },
        { id: 'n2', label: '' },
        { id: 'n3', label: '' }
      ],
      edges: [
        { from: 'n1', to: 'n2' },
        { from: 'n1', to: 'n3' }
      ]
    })
  },
  summary: {
    type: 'summary',
    label: 'Summary',
    description: 'Final recap, 2–3 takeaways.',
    empty: () => ({ eyebrow: 'KEY TAKEAWAYS', title: '', takeaways: ['', ''] })
  }
};

// One-line description shown under the scene tag (prototype `sc.summaryLine`).
export function sceneSummaryLine(type: SceneType, content: Record<string, any>): string {
  switch (type) {
    case 'statement':
      return content.statement || '';
    case 'stat_highlight':
      return [content.value, content.label].filter(Boolean).join(' · ');
    default:
      return content.title || '';
  }
}

// Rebuild diagram edges for a layout from the node list (mirrors the server's normaliser).
export function diagramEdgesFor(
  layout: DiagramLayout,
  nodes: Array<{ id: string }>
): Array<{ from: string; to: string }> {
  if (layout === 'spoke_and_wheel' || layout === 'hierarchy')
    return nodes.slice(1).map((n) => ({ from: nodes[0].id, to: n.id }));
  if (layout === 'flow') return nodes.slice(1).map((n, i) => ({ from: nodes[i].id, to: n.id }));
  return [];
}

// Lightweight client validation for the "incomplete" markers (server is authoritative).
export function sceneProblems(type: SceneType, content: Record<string, any>): string[] {
  const p: string[] = [];
  const req = (v: unknown, name: string) => {
    if (typeof v !== 'string' || !v.trim()) p.push(`${name} is required`);
  };
  const list = (v: unknown, name: string, min: number, max: number) => {
    const arr = Array.isArray(v) ? v : [];
    if (arr.length < min || arr.length > max) p.push(`${name}: ${min}–${max} items`);
    arr.forEach((x, i) => {
      if (typeof x !== 'string' || !x.trim()) p.push(`${name} ${i + 1} is empty`);
    });
  };
  switch (type) {
    case 'statement':
      req(content.statement, 'Statement');
      break;
    case 'key_points':
      req(content.title, 'Title');
      list(content.points, 'Points', LIMITS.keyPoints.min, LIMITS.keyPoints.max);
      break;
    case 'two_column':
      req(content.title, 'Title');
      req(content.body, 'Body');
      break;
    case 'numbered_steps':
      req(content.title, 'Title');
      list(content.steps, 'Steps', LIMITS.steps.min, LIMITS.steps.max);
      break;
    case 'scenario_card':
      req(content.label, 'Label');
      req(content.title, 'Title');
      req(content.body, 'Scenario');
      break;
    case 'stat_highlight':
      req(content.value, 'Value');
      req(content.label, 'Label');
      break;
    case 'diagram':
      req(content.title, 'Title');
      (content.nodes || []).forEach((n: any, i: number) => req(n?.label, `Node ${i + 1}`));
      if (content.layout === 'grid_2x2' && (content.nodes || []).length !== 4)
        p.push('A 2×2 grid needs exactly 4 nodes');
      break;
    case 'summary':
      req(content.eyebrow, 'Eyebrow');
      req(content.title, 'Title');
      list(content.takeaways, 'Takeaways', LIMITS.takeaways.min, LIMITS.takeaways.max);
      break;
    default:
      break;
  }
  return p;
}
